import _ from "lodash";
import React from "react";
import { GetStaticProps, NextPage } from "next";
import Head from "next/head";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import * as CmsHttp from "@app/http/cms";
import { META_DESCRIPTION, META_URL, REVALIDATE_SECONDS } from "@app/utilities/constants";
import { Expert, OfficeRole } from "@models/Expert";
import { IExpertFiltersResponse } from "@app/http/cms";
import SimpleExpertLinkCard from "@components/experts/SimpleExpertLinkCard";
import DropdownComp, { IDropdownItemComp } from "@components/forms/DropdownComp";
import { useState } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import Loader from "@components/common/Loader";

interface ITeamsPage {
  experts: Expert[];
  filters: IExpertFiltersResponse;
}

type TeamsFilterType = {
  [key: string]: {
    selectedItem: IDropdownItemComp;
    items: IDropdownItemComp[];
  };
};

const getFilterDropdownItems = (filters: IExpertFiltersResponse) => {
  const filterRoleItems: IDropdownItemComp[] = [
      {
        key: 0,
        label: "All Roles",
      },
      ...filters.roles.map((elem) => ({
        key: elem,
        label: OfficeRole[elem.trim()],
      })),
    ],
    filterLocationItems: IDropdownItemComp[] = [
      {
        key: 0,
        label: "All Locations",
      },
      ...filters.geolocations.map((elem) => ({
        key: elem.id,
        label: elem.name?.trim(),
      })),
    ],
    filterAoeItems: IDropdownItemComp[] = [
      {
        key: 0,
        label: "All Expertises",
      },
      ...filters.areaOfExpertises.map((elem) => ({
        key: elem.id,
        label: elem.name?.trim(),
      })),
    ],
    filterIssuesTreatedItems: IDropdownItemComp[] = [
      {
        key: 0,
        label: "All Issues Treated",
      },
      ...filters.issuesTreated.map((elem) => ({
        key: elem.id,
        label: elem.name?.trim(),
      })),
    ];

  return {
    filterRoleItems,
    filterLocationItems,
    filterAoeItems,
    filterIssuesTreatedItems,
  };
};

const TeamsPage: NextPage<ITeamsPage> = (props: ITeamsPage) => {
  const { experts: ssrExperts, filters: ssrFilterResponse } = props,
    [experts, setExperts] = useState<Expert[]>(ssrExperts),
    [loading, setLoading] = useState(false),
    [showFilterModal, setShowFilterModal] = useState(false);

  const { filterRoleItems, filterLocationItems, filterAoeItems, filterIssuesTreatedItems } = getFilterDropdownItems(ssrFilterResponse),
    [filterState, setFilterState] = useState<TeamsFilterType>({
      role: {
        selectedItem: null,
        items: filterRoleItems,
      },
      locations: {
        selectedItem: null,
        items: filterLocationItems,
      },
      areaOfExpertises: {
        selectedItem: null,
        items: filterAoeItems,
      },
      issuesTreated: {
        selectedItem: null,
        items: filterIssuesTreatedItems,
      },
    });

  const doApplyFilter = (filterState: TeamsFilterType) => {
    const selectedRole = filterState.role.selectedItem,
      selectedLocation = filterState.locations.selectedItem,
      selectedAoe = filterState.areaOfExpertises.selectedItem,
      selectedIssues = filterState.issuesTreated.selectedItem,
      searchParams = _.omitBy(
        {
          role: selectedRole?.key || "",
          locationId: selectedLocation?.key || "",
          aoeServiceId: selectedAoe?.key || "",
          issuesServiceId: selectedIssues?.key || "",
        },
        // Remove empty values
        (value) => !value
      ),
      requestUri =
        "/api/search-experts?" +
        Object.keys(searchParams)
          .map((key) => key + "=" + searchParams[key])
          .join("&");

    setLoading(true);
    return axios
      .get<{
        expertsResults: Expert[];
        expertsFilters: IExpertFiltersResponse;
      }>(requestUri)
      .then((res) => {
        const {
          data: { expertsResults },
        } = res;
        setExperts(expertsResults);
        return res;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onRoleFilterSelected = (key: string) => {
    const selectedItem = filterRoleItems.find((elem) => elem?.key == key);
    if (selectedItem) {
      const newFilterState: TeamsFilterType = {
        ...filterState,
        role: {
          selectedItem: selectedItem,
          items: filterRoleItems,
        },
      };

      // Update filter state to show that the filter has been applied
      setFilterState(newFilterState);
      doApplyFilter(newFilterState);
    }
  };

  const onLocationFilterSelected = (key: string) => {
    const selectedItem = filterLocationItems.find((elem) => elem?.key == key);
    if (selectedItem) {
      const newFilterState: TeamsFilterType = {
        ...filterState,
        locations: {
          selectedItem: selectedItem,
          items: filterLocationItems,
        },
      };

      // Update filter state to show that the filter has been applied
      // setFilterState(newFilterState);
      doApplyFilter(newFilterState).then((res) => {
        const {
          data: { expertsFilters },
        } = res;

        // Update filter state to show the latest dropdown items based on the result.
        const newFilterDropdownItems = getFilterDropdownItems(expertsFilters);
        newFilterState.areaOfExpertises.items = newFilterDropdownItems.filterAoeItems;
        newFilterState.issuesTreated.items = newFilterDropdownItems.filterIssuesTreatedItems;

        setFilterState(newFilterState);
      });
    }
  };

  const onAoeFilterSelected = (key: string) => {
    const selectedItem = filterAoeItems.find((elem) => elem?.key == key);
    if (selectedItem) {
      const newFilterState: TeamsFilterType = {
        ...filterState,
        areaOfExpertises: {
          selectedItem: selectedItem,
          items: filterAoeItems,
        },
      };

      // Update filter state to show that the filter has been applied
      // setFilterState(newFilterState);
      doApplyFilter(newFilterState).then((res) => {
        const {
          data: { expertsFilters },
        } = res;

        // Update filter state to show the latest dropdown items based on the result.
        const newFilterDropdownItems = getFilterDropdownItems(expertsFilters);
        newFilterState.locations.items = newFilterDropdownItems.filterLocationItems;
        newFilterState.issuesTreated.items = newFilterDropdownItems.filterIssuesTreatedItems;

        setFilterState(newFilterState);
      });
    }
  };

  const onIssuesFilterSelected = (key: string) => {
    const selectedItem = filterIssuesTreatedItems.find((elem) => elem?.key == key);
    if (selectedItem) {
      const newFilterState: TeamsFilterType = {
        ...filterState,
        issuesTreated: {
          selectedItem: selectedItem,
          items: filterIssuesTreatedItems,
        },
      };

      // Update filter state to show that the filter has been applied
      // setFilterState(newFilterState);
      doApplyFilter(newFilterState).then((res) => {
        const {
          data: { expertsFilters },
        } = res;

        // Update filter state to show the latest dropdown items based on the result.
        const newFilterDropdownItems = getFilterDropdownItems(expertsFilters);
        newFilterState.locations.items = newFilterDropdownItems.filterLocationItems;
        newFilterState.areaOfExpertises.items = newFilterDropdownItems.filterAoeItems;

        setFilterState(newFilterState);
      });
    }
  };

  const doShowFilterModal = () => {
    setShowFilterModal(true);
  };

  const doHideFilterModal = () => {
    setShowFilterModal(false);
  };

  /**
   * Meta contents
   */
  const metaTitle = `Team | Heritage Counseling`,
    metaDescription = META_DESCRIPTION || "",
    metaUrl = `${META_URL}/teams`,
    metaImage = `${META_URL}/logo.png`;

  return (
    <PageLayoutV1 title="Team">
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaImage} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaImage} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />

        {experts?.length > 0 &&
          experts.map((expert) => (
            <script
              key={expert.slug}
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(expert.schema),
              }}
            ></script>
          ))}
      </Head>
      <div className="teams-page" data-testid="teams-page">
        <div className="container">
          <div className="filter-container-desktop">
            <div className="filter-wrapper">
              <label htmlFor="role-filter">Role</label>
              <DropdownComp id="role-filter" className="filter-dropdown" onItemSelected={onRoleFilterSelected} items={filterState.role.items} toggleElem={filterState.role.selectedItem?.label || "All Roles"} />
            </div>
            <div className="filter-wrapper">
              <label htmlFor="location-filter">Location</label>
              <DropdownComp id="location-filter" className="filter-dropdown" onItemSelected={onLocationFilterSelected} items={filterState.locations.items} toggleElem={filterState.locations.selectedItem?.label || "All Locations"} />
            </div>
            <div className="filter-wrapper">
              <label htmlFor="aoe-filter">Area of Expertise</label>
              <DropdownComp id="aoe-filter" className="filter-dropdown" onItemSelected={onAoeFilterSelected} items={filterState.areaOfExpertises.items} toggleElem={filterState.areaOfExpertises.selectedItem?.label || "All Expertises"} />
            </div>
            <div className="filter-wrapper">
              <label htmlFor="issues-filter">Issues Treated</label>
              <DropdownComp id="issues-filter" className="filter-dropdown" onItemSelected={onIssuesFilterSelected} items={filterState.issuesTreated.items} toggleElem={filterState.issuesTreated.selectedItem?.label || "All Issues Treated"} />
            </div>
          </div>
          <div className="filter-container-mobile">
            <button type="button" title="Filter" aria-label="Filter" role="form" className="btn btn-round btn-outline-main" onClick={doShowFilterModal}>
              <i className="fa fa-filter"></i> Filters
            </button>
            <Modal className="standard teams-page-modal-filter" show={showFilterModal} onHide={doHideFilterModal}>
              <Modal.Header closeButton>
                <Modal.Title>Teams Filter</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="filter-wrapper">
                  <label htmlFor="location-filter">Location</label>
                  <DropdownComp id="location-filter" className="filter-dropdown" onItemSelected={onLocationFilterSelected} items={filterState.locations.items} toggleElem={filterState.locations.selectedItem?.label || "All Locations"} />
                </div>
                <div className="filter-wrapper">
                  <label htmlFor="aoe-filter">Area of Expertise</label>
                  <DropdownComp id="aoe-filter" className="filter-dropdown" onItemSelected={onAoeFilterSelected} items={filterState.areaOfExpertises.items} toggleElem={filterState.areaOfExpertises.selectedItem?.label || "All Expertises"} />
                </div>
                <div className="filter-wrapper">
                  <label htmlFor="issues-filter">Issues Treated</label>
                  <DropdownComp
                    id="issues-filter"
                    className="filter-dropdown"
                    onItemSelected={onIssuesFilterSelected}
                    items={filterState.issuesTreated.items}
                    toggleElem={filterState.issuesTreated.selectedItem?.label || "All Issues Treated"}
                  />
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button type="button" title="Close Filter" aria-label="Close Filter" role="form" className="btn btn-round btn-outline-main" onClick={doHideFilterModal}>
                  Close
                </button>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
        {loading ? (
          <Loader size={2} />
        ) : experts && experts.length ? (
          <div className="grid-container">
            {experts.map((expert, idx) => (
              <SimpleExpertLinkCard key={idx} expert={expert} />
            ))}
          </div>
        ) : (
          <p className="text-center">No experts found</p>
        )}
      </div>
    </PageLayoutV1>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const [experts, filters] = await Promise.all([CmsHttp.getExperts(), CmsHttp.getExpertsFilters()]);

  return {
    props: {
      experts: experts || [],
      filters,
    } as ITeamsPage,
    notFound: !experts.length,
    revalidate: REVALIDATE_SECONDS, // In seconds
  };
};

export default TeamsPage;
